/**
 * MessagingPage.tsx
 *
 * Full-page wrapper for engagement messaging, used for deep-link navigation.
 * When a notification deep-link points to /messages/:engagementId?threadId=xxx,
 * this page renders the EngagementThreadView in a full-page context with
 * navigation back to the originating workspace.
 *
 * Enterprise tone, no emojis.
 */

import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Loader2, MessageSquare } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { EngagementThreadView } from './messaging/EngagementThreadView';

interface MessagingPageProps {
  accessToken: string;
  currentUser: any;
  engagementId: string;
  threadId?: string;
  onBack?: () => void;
}

export function MessagingPage({
  accessToken,
  currentUser,
  engagementId,
  threadId,
  onBack,
}: MessagingPageProps) {
  const [engagementName, setEngagementName] = useState('Engagement');
  const [configItems, setConfigItems] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    resolveEngagementContext();
  }, [engagementId]);

  const resolveEngagementContext = async () => {
    try {
      setLoading(true);

      // Use the lightweight context endpoint (works for all roles)
      const contextResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-messaging/${engagementId}/context`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      let companyIdForItems: string | null = null;

      if (contextResponse.ok) {
        const ctx = await contextResponse.json();
        if (ctx.engagementName) {
          setEngagementName(ctx.engagementName);
        }
        companyIdForItems = ctx.companyId || null;
      }

      // Fetch config items if we resolved a companyId
      const ciTargetId = companyIdForItems || engagementId;
      const ciResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${ciTargetId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (ciResponse.ok) {
        const data = await ciResponse.json();
        const items = data.items || [];
        setConfigItems(
          items.map((ci: any) => ({
            id: ci.id,
            name: ci.name || ci.title || 'Unnamed',
          })),
        );
      }
    } catch (err) {
      console.error('[MessagingPage] Error resolving context:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-slate-400" />
        <span className="ml-3 text-sm text-slate-500">Loading messages</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="size-5 text-blue-500" />
              <div>
                <h1 className="text-base font-semibold text-slate-900">
                  Engagement Messages
                </h1>
                <p className="text-xs text-slate-500">{engagementName}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Thread View */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="h-[calc(100vh-12rem)]">
            <EngagementThreadView
              engagementId={engagementId}
              engagementName={engagementName}
              accessToken={accessToken}
              currentUser={currentUser}
              configItems={configItems}
              onClose={handleBack}
              initialThreadId={threadId}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}